import { useEffect } from 'react'
import Router from 'next/router'

/*
  Defensive guard for a Next.js head-manager crash:
  `TypeError: Cannot read properties of null (reading 'content')` thrown at
  `Number(headCountEl.content)` in next/dist/client/head-manager.js.

  It fires when the SSR-emitted `<meta name="next-head-count">` element is
  removed from <head> after hydration — typically by headless/stealth bots,
  translation tools, or extensions that rewrite the head. Next.js's own
  `!headCountEl` guard is compiled out in production, so the missing meta
  becomes a hard clientside error on the next route-change head update.
*/

const NEXT_HEAD_COUNT = 'next-head-count'

// Re-insert next-head-count if it's missing. The content is set to the current
// head element count so the head-manager's backward sibling walk still reaches
// every managed tag and de-dupes correctly instead of duplicating tags.
const ensureNextHeadCount = (): void => {
  if (typeof document === 'undefined') return

  const head = document.head
  if (!head || head.querySelector(`meta[name="${NEXT_HEAD_COUNT}"]`)) return

  const meta = document.createElement('meta')
  meta.name = NEXT_HEAD_COUNT
  meta.content = String(head.childElementCount)
  head.appendChild(meta)
}

// Runs the guard once on mount, then keeps the meta in place just before each
// client-side head reconciliation runs.
const useNextHeadCountGuard = (): void => {
  useEffect(() => {
    ensureNextHeadCount()
    Router.events.on('routeChangeStart', ensureNextHeadCount)
    Router.events.on('beforeHistoryChange', ensureNextHeadCount)

    return () => {
      Router.events.off('routeChangeStart', ensureNextHeadCount)
      Router.events.off('beforeHistoryChange', ensureNextHeadCount)
    }
  }, [])
}

export default useNextHeadCountGuard
